import { useEffect, useState, type CSSProperties } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { logout, whoami } from "../../lib/auth";
import type { SessionUser } from "../../types/auth";
import KioskControls from "../common/KioskControls";
import "./HostLayout.css";

const HOST_ROLES = new Set(["bar_host", "bar_manager", "bar_owner", "manager", "owner"]);
function canAccessHost(roles: string[]) { return roles.some((r) => HOST_ROLES.has(r)); }

export default function HostLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    void whoami().then((u) => {
      if (!mounted) return;
      setUser(u);
      setLoaded(true);
      if (!u) {
        navigate("/login", { replace: true });
        return;
      }
      if (!canAccessHost(u.bar_roles)) {
        navigate("/", { replace: true });
      }
    });
    return () => {
      mounted = false;
    };
  }, [navigate]);

  function onLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (!loaded) {
    return (
      <main style={styles.loading}>
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      </main>
    );
  }
  if (!user || !canAccessHost(user.bar_roles)) return null;

  return (
    <div style={styles.shell}>
      <a href="#host-content" className="skip-link sr-only">
        Skip to content
      </a>
      <header className="host-header" style={styles.header}>
        <div className="host-header-brand" style={styles.headerTop}>
          <div style={styles.brand}>
            <NavLink to="/" title="Return to Sentinel OS Hub" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img
                src="/img/sentinel-logo.svg"
                alt="The Sentinel Room"
                style={{ height: 26, width: "auto", display: "block" }}
              />
            </NavLink>
            <div className="host-brand-text" style={styles.brandText}>
              <p style={styles.eyebrow}>The Sentinel · Ledger</p>
              <p style={styles.subEyebrow}>{user.display_label}</p>
            </div>
          </div>
          <nav className="host-header-aux" aria-label="Host account navigation">
          {user.bar_roles.some((r) => ["bar_manager", "bar_owner"].includes(r)) && (
            <NavLink
              to="/"
              style={styles.navLink}
            >
              ← OS Hub
            </NavLink>
          )}
          {user.bar_roles.includes("bar_owner") && (
            <NavLink
              to="/host/settings"
              style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}
            >
              Settings
            </NavLink>
          )}
          </nav>
        </div>
        <div className="host-header-toolbar">
        <nav className="host-primary-nav" aria-label="Host navigation">
          <NavLink
            to="/host/intake"
            style={styles.addPartyBtn}
          >
            + Add Party
          </NavLink>
          <NavLink
            to="/host/dashboard"
            style={({ isActive }) => ({
              ...styles.navLink,
              ...(isActive ? styles.navLinkActive : {}),
            })}
          >
            Dashboard
          </NavLink>
        </nav>
        <KioskControls compact autoKeepAwake responsive />
        </div>
      </header>

      <main id="host-content" style={styles.content}>
        <Outlet context={{ user }} />
      </main>

      <footer style={styles.footer}>
        <button type="button" onClick={onLogout} style={styles.footerSignOut}>
          Sign out
        </button>
      </footer>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  loading: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  shell: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    borderBottom: "1px solid var(--border)",
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minWidth: 0,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  },
  brandText: {
    minWidth: 0,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  subEyebrow: {
    margin: "2px 0 0",
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  navLink: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    textDecoration: "none",
    color: "var(--muted)",
    border: "1px solid var(--border)",
  },
  navLinkActive: {
    color: "var(--gold)",
    borderColor: "var(--gold)",
  },
  addPartyBtn: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    textDecoration: "none",
    color: "#0D0B08",
    background: "var(--gold)",
    border: "1px solid var(--gold)",
    boxShadow: "0 2px 8px rgba(249, 175, 54, 0.2)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: 16,
  },
  footer: {
    paddingTop: 24,
    paddingBottom: 24,
    borderTop: "1px solid var(--border)",
    textAlign: "center",
  },
  footerSignOut: {
    background: "transparent",
    color: "var(--muted)",
    border: 0,
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    padding: "8px 16px",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textDecoration: "underline",
    textUnderlineOffset: 3,
  },
};
